import React, { useState, useMemo, useRef, useCallback } from 'react'
import {
  getPurchases, savePurchase, deletePurchase, getSuppliers, saveSupplier,
  getInventoryItems, getAllPosProducts
} from '../../lib/posStore'
import { parseInvoice } from '../../lib/invoiceParser'
import BankStatementsTab from '../../components/purchases/BankStatementsTab'
import { useDepartment } from '../../context/DepartmentContext'
import {
  Plus, Search, Upload, FileText, DollarSign, Filter,
  Check, X, Download, Calendar, ShoppingBag, Loader, Trash2, ChevronDown, ChevronUp,
  UserPlus, Eye, AlertTriangle, Sparkles, Image as ImageIcon
} from 'lucide-react'

const today = () => new Date().toISOString().split('T')[0]

const emptyLineItem = { inventoryItemId: '', name: '', qty: '', unitPrice: '' }

function formatR(n) {
  return `R${Number(n || 0).toFixed(2)}`
}

export default function Purchases() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('orders')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showForm, setShowForm] = useState(false)

  // Purchase Order form
  const [poForm, setPoForm] = useState({
    supplierId: '', date: today(), lineItems: [{ ...emptyLineItem }], status: 'draft', invoiceFile: null
  })
  const [editingId, setEditingId] = useState(null)

  // Quick Entry form
  const [quickForm, setQuickForm] = useState({
    supplierId: '', date: today(), lineItems: [{ ...emptyLineItem }]
  })
  const [quickSaved, setQuickSaved] = useState(false)

  // Analytics
  // Invoice Upload state
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadPreview, setUploadPreview] = useState(null)
  const [parsing, setParsing] = useState(false)
  const [parseProgress, setParseProgress] = useState(0)
  const [parseResult, setParseResult] = useState(null)
  const [parseError, setParseError] = useState(null)
  const [showRawText, setShowRawText] = useState(false)
  const [newSupplierForm, setNewSupplierForm] = useState(null)
  const [uploadLineItems, setUploadLineItems] = useState([])
  const [uploadSupplierId, setUploadSupplierId] = useState('')
  const [uploadDate, setUploadDate] = useState('')
  const fileInputRef = useRef(null)

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  // Expanded row for viewing details
  const [expandedRow, setExpandedRow] = useState(null)

  const [analyticsFrom, setAnalyticsFrom] = useState(() => {
    const d = new Date()
    d.setMonth(d.getMonth() - 1)
    return d.toISOString().split('T')[0]
  })
  const [analyticsTo, setAnalyticsTo] = useState(today())

  const suppliers = useMemo(() => getSuppliers(currentDepartmentId), [currentDepartmentId, tab, showForm])
  const inventoryItems = useMemo(() => getInventoryItems(currentDepartmentId), [currentDepartmentId])
  const products = useMemo(() => getAllPosProducts(currentDepartmentId), [currentDepartmentId])

  // Combined list of items for dropdowns
  const allItems = useMemo(() => {
    const items = []
    for (const inv of inventoryItems) {
      items.push({ id: inv.id, name: inv.name || inv.id, type: 'inventory' })
    }
    for (const prod of products) {
      if (!items.find(i => i.name === prod.name)) {
        items.push({ id: prod.id, name: prod.name || prod.id, type: 'product' })
      }
    }
    return items.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [inventoryItems, products])

  const purchases = useMemo(() => getPurchases(currentDepartmentId), [currentDepartmentId, showForm, tab, quickSaved])

  const filteredPurchases = useMemo(() => {
    let list = purchases
    if (statusFilter) list = list.filter(p => p.status === statusFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(p => {
        const supplierName = suppliers.find(s => s.id === p.supplierId)?.name || ''
        return supplierName.toLowerCase().includes(q) || p.date?.includes(q)
      })
    }
    return list
  }, [purchases, search, statusFilter, suppliers])

  // --- Analytics ---
  const analytics = useMemo(() => {
    const filtered = getPurchases(currentDepartmentId, null, analyticsFrom, analyticsTo)
    const totalSpend = filtered.reduce((s, p) => s + (Number(p.total) || 0), 0)

    const bySupplier = {}
    const byCategory = {}
    for (const p of filtered) {
      const sup = suppliers.find(s => s.id === p.supplierId)
      const supName = sup?.name || 'Unknown'
      const supCat = sup?.category || 'Other'
      bySupplier[supName] = (bySupplier[supName] || 0) + (Number(p.total) || 0)
      byCategory[supCat] = (byCategory[supCat] || 0) + (Number(p.total) || 0)
    }

    const supplierList = Object.entries(bySupplier).sort((a, b) => b[1] - a[1])
    const categoryList = Object.entries(byCategory).sort((a, b) => b[1] - a[1])

    return { totalSpend, supplierList, categoryList, count: filtered.length }
  }, [currentDepartmentId, analyticsFrom, analyticsTo, suppliers, tab])

  // --- Helpers ---
  function getSupplierName(id) {
    return suppliers.find(s => s.id === id)?.name || 'Unknown'
  }

  function lineTotal(item) {
    return (Number(item.qty) || 0) * (Number(item.unitPrice) || 0)
  }

  function calcOrderTotal(items) {
    return items.reduce((s, item) => s + lineTotal(item), 0)
  }

  // --- Purchase Order CRUD ---
  function openNewOrder() {
    setPoForm({ supplierId: '', date: today(), lineItems: [{ ...emptyLineItem }], status: 'draft', invoiceFile: null })
    setEditingId(null)
    setShowForm(true)
  }

  function openEditOrder(purchase) {
    setPoForm({
      supplierId: purchase.supplierId || '',
      date: purchase.date || today(),
      lineItems: purchase.lineItems?.length ? purchase.lineItems.map(li => ({ ...li })) : [{ ...emptyLineItem }],
      status: purchase.status || 'draft',
      invoiceFile: null
    })
    setEditingId(purchase.id)
    setShowForm(true)
  }

  function updateLineItem(index, field, value) {
    const items = [...poForm.lineItems]
    items[index] = { ...items[index], [field]: value }
    // If selecting from dropdown, set the inventoryItemId
    if (field === 'inventoryItemId') {
      const match = allItems.find(i => i.id === value)
      if (match) items[index].name = match.name
    }
    setPoForm({ ...poForm, lineItems: items })
  }

  function addLineItem() {
    setPoForm({ ...poForm, lineItems: [...poForm.lineItems, { ...emptyLineItem }] })
  }

  function removeLineItem(index) {
    if (poForm.lineItems.length <= 1) return
    const items = poForm.lineItems.filter((_, i) => i !== index)
    setPoForm({ ...poForm, lineItems: items })
  }

  function handleSaveOrder(status) {
    if (!poForm.supplierId) return
    const total = calcOrderTotal(poForm.lineItems)
    const record = {
      ...(editingId ? { id: editingId } : {}),
      departmentId: currentDepartmentId,
      supplierId: poForm.supplierId,
      date: poForm.date,
      lineItems: poForm.lineItems.filter(li => li.name || li.inventoryItemId),
      total,
      status: status || poForm.status,
      invoiceFileName: poForm.invoiceFile?.name || null
    }
    savePurchase(record)
    setShowForm(false)
  }

  // --- Quick Entry ---
  function updateQuickItem(index, field, value) {
    const items = [...quickForm.lineItems]
    items[index] = { ...items[index], [field]: value }
    if (field === 'inventoryItemId') {
      const match = allItems.find(i => i.id === value)
      if (match) items[index].name = match.name
    }
    setQuickForm({ ...quickForm, lineItems: items })
  }

  function addQuickItem() {
    setQuickForm({ ...quickForm, lineItems: [...quickForm.lineItems, { ...emptyLineItem }] })
  }

  function removeQuickItem(index) {
    if (quickForm.lineItems.length <= 1) return
    const items = quickForm.lineItems.filter((_, i) => i !== index)
    setQuickForm({ ...quickForm, lineItems: items })
  }

  function handleQuickSave() {
    const validItems = quickForm.lineItems.filter(li => li.name || li.inventoryItemId)
    if (validItems.length === 0) return
    const total = calcOrderTotal(validItems)
    savePurchase({
      departmentId: currentDepartmentId,
      supplierId: quickForm.supplierId || null,
      date: quickForm.date,
      lineItems: validItems,
      total,
      status: 'confirmed',
      source: 'quick-entry'
    })
    setQuickForm({ supplierId: '', date: today(), lineItems: [{ ...emptyLineItem }] })
    setQuickSaved(prev => !prev)
  }

  const statusBadge = (status) => {
    const styles = {
      draft: 'bg-yellow-900/40 text-yellow-300',
      confirmed: 'bg-green-900/40 text-green-300',
    }
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-gray-800 text-gray-400'}`}>
        {status || 'draft'}
      </span>
    )
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Purchases & Invoices</h1>
          <p className="text-gray-400 text-sm mt-1">{purchases.length} purchase order{purchases.length !== 1 ? 's' : ''} recorded</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-900 border border-gray-800 rounded-lg p-1 w-fit">
          {[
            { key: 'orders', label: 'Purchase Orders' },
            { key: 'upload', label: 'Invoice Upload' },
            { key: 'quick', label: 'Quick Entry' },
            { key: 'analytics', label: 'Spend Analytics' },
            { key: 'bank', label: 'Bank Statements' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                tab === t.key ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ===== TAB 1: PURCHASE ORDERS ===== */}
        {tab === 'orders' && (
          <>
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by supplier or date..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-gray-600"
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-gray-600"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="confirmed">Confirmed</option>
              </select>
              <button
                onClick={openNewOrder}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition"
              >
                <Plus size={16} /> New Order
              </button>
            </div>

            {/* Purchase Table */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              {filteredPurchases.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <ShoppingBag size={40} className="mx-auto mb-3 opacity-40" />
                  <p className="text-lg">No purchase orders found</p>
                  <p className="text-sm mt-1">Create your first purchase order</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left text-gray-400 font-medium px-4 py-3">Date</th>
                        <th className="text-left text-gray-400 font-medium px-4 py-3">Supplier</th>
                        <th className="text-center text-gray-400 font-medium px-4 py-3">Items</th>
                        <th className="text-right text-gray-400 font-medium px-4 py-3">Total</th>
                        <th className="text-center text-gray-400 font-medium px-4 py-3">Status</th>
                        <th className="text-center text-gray-400 font-medium px-4 py-3">Invoice</th>
                        <th className="text-right text-gray-400 font-medium px-4 py-3 w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPurchases.map(p => (
                        <React.Fragment key={p.id}>
                          <tr className="border-b border-gray-800/50 hover:bg-gray-800/30 group cursor-pointer" onClick={() => setExpandedRow(expandedRow === p.id ? null : p.id)}>
                            <td className="px-4 py-3 text-gray-300">
                              <div className="flex items-center gap-1.5">
                                {expandedRow === p.id ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
                                {p.date}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-white font-medium">{getSupplierName(p.supplierId)}</td>
                            <td className="px-4 py-3 text-center text-gray-300">{p.lineItems?.length || 0}</td>
                            <td className="px-4 py-3 text-right text-white font-semibold">{formatR(p.total)}</td>
                            <td className="px-4 py-3 text-center">{statusBadge(p.status)}</td>
                            <td className="px-4 py-3 text-center">
                              {p.invoiceFileName ? (
                                <span className="inline-flex items-center gap-1 text-xs text-blue-400">
                                  <FileText size={12} /> {p.invoiceFileName}
                                </span>
                              ) : (
                                <span className="text-gray-600 text-xs">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition">
                                {p.status === 'draft' && (
                                  <button
                                    onClick={() => {
                                      savePurchase({ ...p, departmentId: currentDepartmentId, status: 'confirmed' })
                                      setShowForm(prev => !prev)
                                    }}
                                    title="Confirm & update stock"
                                    className="p-1.5 rounded bg-green-900/40 hover:bg-green-900/70 text-green-400 transition"
                                  >
                                    <Check size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => openEditOrder(p)}
                                  title="Edit"
                                  className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition"
                                >
                                  <FileText size={14} />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(p)}
                                  title="Delete"
                                  className="p-1.5 rounded bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-400 transition"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedRow === p.id && (
                            <tr className="border-b border-gray-800/50 bg-gray-800/20">
                              <td colSpan={7} className="px-4 py-4">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-4 text-xs text-gray-400">
                                    <span>Order #{p.id?.slice(-6)}</span>
                                    <span>Created: {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '-'}</span>
                                    {p.invoiceFileName && <span className="flex items-center gap-1"><FileText size={11} /> {p.invoiceFileName}</span>}
                                  </div>
                                  {p.lineItems?.length > 0 ? (
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="text-gray-500 text-xs">
                                          <th className="text-left pb-2 font-medium">Item</th>
                                          <th className="text-center pb-2 font-medium">Qty</th>
                                          <th className="text-right pb-2 font-medium">Unit Price</th>
                                          <th className="text-right pb-2 font-medium">Line Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {p.lineItems.map((li, idx) => {
                                          const itemName = li.name || allItems.find(it => it.id === li.inventoryItemId)?.name || 'Unknown'
                                          return (
                                            <tr key={idx} className="border-t border-gray-800/40">
                                              <td className="py-1.5 text-gray-300">{itemName}</td>
                                              <td className="py-1.5 text-center text-gray-400">{li.qty}</td>
                                              <td className="py-1.5 text-right text-gray-400">{formatR(li.unitPrice)}</td>
                                              <td className="py-1.5 text-right text-white font-medium">{formatR((Number(li.qty) || 0) * (Number(li.unitPrice) || 0))}</td>
                                            </tr>
                                          )
                                        })}
                                      </tbody>
                                      <tfoot>
                                        <tr className="border-t border-gray-700">
                                          <td colSpan={3} className="py-2 text-right text-gray-400 text-xs font-medium">Total</td>
                                          <td className="py-2 text-right text-white font-bold">{formatR(p.total)}</td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  ) : (
                                    <p className="text-gray-500 text-sm">No line items</p>
                                  )}
                                  <div className="flex gap-2 pt-1">
                                    <button
                                      onClick={() => openEditOrder(p)}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg transition"
                                    >
                                      <FileText size={12} /> Edit Order
                                    </button>
                                    <button
                                      onClick={() => setDeleteConfirm(p)}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-400 text-xs rounded-lg transition"
                                    >
                                      <Trash2 size={12} /> Delete
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Purchase Order Form Modal */}
            {showForm && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-5 border-b border-gray-800">
                    <h2 className="text-lg font-semibold">{editingId ? 'Edit Purchase Order' : 'New Purchase Order'}</h2>
                    <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
                  </div>
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Supplier *</label>
                        <select
                          value={poForm.supplierId}
                          onChange={e => setPoForm({ ...poForm, supplierId: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                        >
                          <option value="">Select supplier</option>
                          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Date</label>
                        <input
                          type="date"
                          value={poForm.date}
                          onChange={e => setPoForm({ ...poForm, date: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                        />
                      </div>
                    </div>

                    {/* Line Items */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Line Items</label>
                      <div className="space-y-2">
                        {poForm.lineItems.map((li, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <select
                              value={li.inventoryItemId}
                              onChange={e => updateLineItem(idx, 'inventoryItemId', e.target.value)}
                              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                            >
                              <option value="">Select item...</option>
                              {allItems.map(item => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={li.name}
                              onChange={e => updateLineItem(idx, 'name', e.target.value)}
                              placeholder="Or type name"
                              className="w-32 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                            />
                            <input
                              type="number"
                              value={li.qty}
                              onChange={e => updateLineItem(idx, 'qty', e.target.value)}
                              placeholder="Qty"
                              className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                            />
                            <input
                              type="number"
                              step="0.01"
                              value={li.unitPrice}
                              onChange={e => updateLineItem(idx, 'unitPrice', e.target.value)}
                              placeholder="Price"
                              className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                            />
                            <span className="text-gray-400 text-sm w-20 text-right">{formatR(lineTotal(li))}</span>
                            <button
                              onClick={() => removeLineItem(idx)}
                              className="p-1.5 text-gray-500 hover:text-red-400 transition"
                              title="Remove"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={addLineItem}
                        className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs mt-2 transition"
                      >
                        <Plus size={14} /> Add line item
                      </button>
                    </div>

                    {/* Total */}
                    <div className="flex justify-end border-t border-gray-800 pt-4">
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Order Total</p>
                        <p className="text-2xl font-bold text-white">{formatR(calcOrderTotal(poForm.lineItems))}</p>
                      </div>
                    </div>

                    {/* Invoice Upload */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Invoice Attachment</label>
                      <label className="flex items-center gap-2 bg-gray-800 border border-gray-700 border-dashed rounded-lg px-4 py-3 cursor-pointer hover:border-gray-500 transition">
                        <Upload size={16} className="text-gray-500" />
                        <span className="text-sm text-gray-400">
                          {poForm.invoiceFile ? poForm.invoiceFile.name : 'Upload PDF or image'}
                        </span>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          className="hidden"
                          onChange={e => setPoForm({ ...poForm, invoiceFile: e.target.files?.[0] || null })}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-between gap-3 p-5 border-t border-gray-800">
                    <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition">
                      Cancel
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveOrder('draft')}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition"
                      >
                        Save as Draft
                      </button>
                      <button
                        onClick={() => handleSaveOrder('confirmed')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition"
                      >
                        <Check size={14} /> Confirm & Update Stock
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
                <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                  <div className="p-6 text-center">
                    <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Trash2 size={24} className="text-red-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">Delete Purchase Order?</h3>
                    <p className="text-sm text-gray-400 mb-1">
                      {getSupplierName(deleteConfirm.supplierId)} — {deleteConfirm.date}
                    </p>
                    <p className="text-sm text-gray-400 mb-1">
                      {deleteConfirm.lineItems?.length || 0} items · {formatR(deleteConfirm.total)}
                    </p>
                    {deleteConfirm.status === 'confirmed' && (
                      <p className="text-xs text-yellow-400 mt-3">
                        <AlertTriangle size={12} className="inline mr-1" />
                        This order was confirmed. Stock adjustments will not be reversed.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-3 p-4 border-t border-gray-800">
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="flex-1 px-4 py-2.5 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        deletePurchase(deleteConfirm.id)
                        setDeleteConfirm(null)
                        setExpandedRow(null)
                        setShowForm(prev => !prev) // trigger re-render
                      }}
                      className="flex-1 px-4 py-2.5 text-sm text-white bg-red-600 hover:bg-red-500 rounded-lg font-medium transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== TAB 2: INVOICE UPLOAD ===== */}
        {tab === 'upload' && (
          <InvoiceUploadTab
            suppliers={suppliers}
            allItems={allItems}
            currentDepartmentId={currentDepartmentId}
            uploadFile={uploadFile}
            setUploadFile={setUploadFile}
            uploadPreview={uploadPreview}
            setUploadPreview={setUploadPreview}
            parsing={parsing}
            setParsing={setParsing}
            parseProgress={parseProgress}
            setParseProgress={setParseProgress}
            parseResult={parseResult}
            setParseResult={setParseResult}
            parseError={parseError}
            setParseError={setParseError}
            showRawText={showRawText}
            setShowRawText={setShowRawText}
            newSupplierForm={newSupplierForm}
            setNewSupplierForm={setNewSupplierForm}
            uploadLineItems={uploadLineItems}
            setUploadLineItems={setUploadLineItems}
            uploadSupplierId={uploadSupplierId}
            setUploadSupplierId={setUploadSupplierId}
            uploadDate={uploadDate}
            setUploadDate={setUploadDate}
            fileInputRef={fileInputRef}
            onPurchaseSaved={() => setQuickSaved(prev => !prev)}
          />
        )}

        {/* ===== TAB 3: QUICK ENTRY ===== */}
        {tab === 'quick' && (
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-5">
              <ShoppingBag size={20} className="text-blue-400" />
              <h2 className="text-lg font-semibold">Quick Purchase Entry</h2>
              <span className="text-xs text-gray-500 ml-2">For cash purchases and quick logging</span>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={quickForm.date}
                    onChange={e => setQuickForm({ ...quickForm, date: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Supplier (optional)</label>
                  <select
                    value={quickForm.supplierId}
                    onChange={e => setQuickForm({ ...quickForm, supplierId: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                  >
                    <option value="">Cash purchase / walk-in</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Quick Items */}
              <div>
                <label className="block text-xs text-gray-400 mb-2">Items</label>
                <div className="space-y-2">
                  {quickForm.lineItems.map((li, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <select
                        value={li.inventoryItemId}
                        onChange={e => updateQuickItem(idx, 'inventoryItemId', e.target.value)}
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                      >
                        <option value="">Select item...</option>
                        {allItems.map(item => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={li.name}
                        onChange={e => updateQuickItem(idx, 'name', e.target.value)}
                        placeholder="Or type name"
                        className="w-36 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                      />
                      <input
                        type="number"
                        value={li.qty}
                        onChange={e => updateQuickItem(idx, 'qty', e.target.value)}
                        placeholder="Qty"
                        className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={li.unitPrice}
                        onChange={e => updateQuickItem(idx, 'unitPrice', e.target.value)}
                        placeholder="Price"
                        className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                      />
                      <span className="text-gray-400 text-sm w-20 text-right">{formatR(lineTotal(li))}</span>
                      <button
                        onClick={() => removeQuickItem(idx)}
                        className="p-1.5 text-gray-500 hover:text-red-400 transition"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addQuickItem}
                  className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs mt-2 transition"
                >
                  <Plus size={14} /> Add item
                </button>
              </div>

              {/* Quick Total & Save */}
              <div className="flex items-center justify-between border-t border-gray-800 pt-4">
                <div>
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-xl font-bold">{formatR(calcOrderTotal(quickForm.lineItems))}</p>
                </div>
                <button
                  onClick={handleQuickSave}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition"
                >
                  <Check size={16} /> Save Purchase
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== TAB 3: SPEND ANALYTICS ===== */}
        {tab === 'analytics' && (
          <>
            {/* Date Range */}
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 mb-6">
              <div>
                <label className="block text-xs text-gray-400 mb-1">From</label>
                <input
                  type="date"
                  value={analyticsFrom}
                  onChange={e => setAnalyticsFrom(e.target.value)}
                  className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">To</label>
                <input
                  type="date"
                  value={analyticsTo}
                  onChange={e => setAnalyticsTo(e.target.value)}
                  className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-600"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Calendar size={14} />
                {analytics.count} orders in range
              </div>
            </div>

            {/* Total Spend Card */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-900/40 flex items-center justify-center">
                  <DollarSign size={24} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Total Spend</p>
                  <p className="text-3xl font-bold text-white">{formatR(analytics.totalSpend)}</p>
                </div>
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              {/* Spend by Supplier */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-gray-300 mb-4">Spend by Supplier</h3>
                {analytics.supplierList.length === 0 ? (
                  <p className="text-sm text-gray-500">No data for this period</p>
                ) : (
                  <div className="space-y-3">
                    {analytics.supplierList.map(([name, amount]) => {
                      const pct = analytics.totalSpend > 0 ? (amount / analytics.totalSpend * 100) : 0
                      return (
                        <div key={name}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-300">{name}</span>
                            <span className="text-white font-medium">{formatR(amount)}</span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Spend by Category */}
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-gray-300 mb-4">Spend by Category</h3>
                {analytics.categoryList.length === 0 ? (
                  <p className="text-sm text-gray-500">No data for this period</p>
                ) : (
                  <div className="space-y-3">
                    {analytics.categoryList.map(([name, amount]) => {
                      const pct = analytics.totalSpend > 0 ? (amount / analytics.totalSpend * 100) : 0
                      const colors = {
                        Meat: 'bg-red-500', Bakery: 'bg-amber-500', Groceries: 'bg-green-500',
                        Beverages: 'bg-blue-500', Packaging: 'bg-purple-500', Other: 'bg-gray-500'
                      }
                      return (
                        <div key={name}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-300">{name}</span>
                            <span className="text-white font-medium">{formatR(amount)} <span className="text-gray-500 text-xs">({pct.toFixed(0)}%)</span></span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${colors[name] || 'bg-gray-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ===== TAB 5: BANK STATEMENTS ===== */}
        {tab === 'bank' && (
          <BankStatementsTab departmentId={currentDepartmentId} />
        )}
      </div>
    </div>
  )
}

// ── Invoice Upload Tab Component ─────────────────────────────────────
const SUPPLIER_CATEGORIES = ['Meat', 'Bakery', 'Groceries', 'Beverages', 'Packaging']

function InvoiceUploadTab({
  suppliers, allItems, currentDepartmentId,
  uploadFile, setUploadFile, uploadPreview, setUploadPreview,
  parsing, setParsing, parseProgress, setParseProgress,
  parseResult, setParseResult, parseError, setParseError,
  showRawText, setShowRawText,
  newSupplierForm, setNewSupplierForm,
  uploadLineItems, setUploadLineItems,
  uploadSupplierId, setUploadSupplierId,
  uploadDate, setUploadDate,
  fileInputRef, onPurchaseSaved,
}) {
  const today = new Date().toISOString().split('T')[0]

  function resetUpload() {
    setUploadFile(null)
    setUploadPreview(null)
    setParseResult(null)
    setParseError(null)
    setParsing(false)
    setParseProgress(0)
    setShowRawText(false)
    setNewSupplierForm(null)
    setUploadLineItems([])
    setUploadSupplierId('')
    setUploadDate('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function handleFileSelect(e) {
    const file = e.target.files?.[0]
    if (!file) return

    resetUpload()
    setUploadFile(file)

    // Create preview for images
    if (file.type?.startsWith('image/')) {
      const url = URL.createObjectURL(file)
      setUploadPreview(url)
    }

    // Start parsing
    setParsing(true)
    setParseError(null)
    setParseProgress(0)

    try {
      const result = await parseInvoice(file, suppliers, (pct) => setParseProgress(pct))
      setParseResult(result)

      // Pre-fill from results
      if (result.supplierMatch) {
        setUploadSupplierId(result.supplierMatch.supplier.id)
      }
      if (result.date) {
        setUploadDate(result.date)
      } else {
        setUploadDate(today)
      }
      if (result.lineItems?.length > 0) {
        // Try to match extracted items to inventory items
        const matched = result.lineItems.map(li => {
          const nameLC = (li.name || '').toLowerCase()
          const match = allItems.find(item =>
            (item.name || '').toLowerCase().includes(nameLC) ||
            nameLC.includes((item.name || '').toLowerCase())
          )
          return {
            ...li,
            inventoryItemId: match?.id || '',
            name: li.name,
          }
        })
        setUploadLineItems(matched)
      } else {
        setUploadLineItems([{ inventoryItemId: '', name: '', qty: '', unitPrice: '' }])
      }
    } catch (err) {
      console.error('Invoice parse error:', err)
      setParseError(err.message || 'Failed to parse invoice')
      setUploadDate(today)
      setUploadLineItems([{ inventoryItemId: '', name: '', qty: '', unitPrice: '' }])
    } finally {
      setParsing(false)
    }
  }

  function handleCreateSupplier() {
    const name = parseResult?.extractedSupplierName || ''
    setNewSupplierForm({
      name: name,
      contactPerson: '',
      phone: '',
      email: '',
      address: '',
      category: 'Groceries',
      paymentTerms: '',
      notes: '',
    })
  }

  function saveNewSupplier() {
    if (!newSupplierForm?.name?.trim()) return
    const record = { ...newSupplierForm, departmentId: currentDepartmentId }
    saveSupplier(record)
    // Find the newly created supplier (it was just saved)
    const updatedSuppliers = getSuppliers(currentDepartmentId)
    const created = updatedSuppliers.find(s => s.name === newSupplierForm.name)
    if (created) {
      setUploadSupplierId(created.id)
    }
    setNewSupplierForm(null)
  }

  function updateUploadLineItem(idx, field, value) {
    const items = [...uploadLineItems]
    items[idx] = { ...items[idx], [field]: value }
    if (field === 'inventoryItemId') {
      const match = allItems.find(i => i.id === value)
      if (match) items[idx].name = match.name
    }
    setUploadLineItems(items)
  }

  function addUploadLineItem() {
    setUploadLineItems([...uploadLineItems, { inventoryItemId: '', name: '', qty: '', unitPrice: '' }])
  }

  function removeUploadLineItem(idx) {
    if (uploadLineItems.length <= 1) return
    setUploadLineItems(uploadLineItems.filter((_, i) => i !== idx))
  }

  function lineTotal(item) {
    return (Number(item.qty) || 0) * (Number(item.unitPrice) || 0)
  }

  function handleSavePurchase(status) {
    const validItems = uploadLineItems.filter(li => li.name || li.inventoryItemId)
    if (validItems.length === 0) return
    const total = validItems.reduce((s, li) => s + lineTotal(li), 0)
    savePurchase({
      departmentId: currentDepartmentId,
      supplierId: uploadSupplierId || null,
      date: uploadDate || today,
      lineItems: validItems,
      total,
      status,
      source: 'invoice-upload',
      invoiceFileName: uploadFile?.name || null,
    })
    onPurchaseSaved?.()
    resetUpload()
  }

  const confidenceColor = (score) => {
    if (score >= 0.9) return 'text-green-400'
    if (score >= 0.7) return 'text-yellow-400'
    return 'text-orange-400'
  }

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      {!uploadFile && (
        <div
          className="border-2 border-dashed border-gray-700 rounded-xl p-12 text-center hover:border-gray-500 transition cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500') }}
          onDragLeave={e => { e.currentTarget.classList.remove('border-blue-500') }}
          onDrop={e => {
            e.preventDefault()
            e.currentTarget.classList.remove('border-blue-500')
            const file = e.dataTransfer.files?.[0]
            if (file && fileInputRef.current) {
              const dt = new DataTransfer()
              dt.items.add(file)
              fileInputRef.current.files = dt.files
              handleFileSelect({ target: { files: [file] } })
            }
          }}
        >
          <Upload size={48} className="mx-auto mb-4 text-gray-600" />
          <p className="text-lg text-gray-300 font-medium mb-2">Upload Invoice</p>
          <p className="text-sm text-gray-500 mb-4">
            Drop an image or PDF here, or click to browse
          </p>
          <p className="text-xs text-gray-600">
            Supports: PDF, PNG, JPG, JPEG — System will auto-detect supplier and extract line items
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.bmp"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      )}

      {/* Parsing Progress */}
      {parsing && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
          <Loader size={32} className="mx-auto mb-4 text-blue-400 animate-spin" />
          <p className="text-white font-medium mb-2">
            {uploadFile?.type?.startsWith('image/') ? 'Running OCR on image...' : 'Extracting text from PDF...'}
          </p>
          {uploadFile?.type?.startsWith('image/') && parseProgress > 0 && (
            <div className="max-w-xs mx-auto">
              <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden mt-2">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${parseProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{parseProgress}%</p>
            </div>
          )}
        </div>
      )}

      {/* Parse Error */}
      {parseError && !parsing && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-red-400 mt-0.5" />
            <div>
              <p className="text-red-300 font-medium">Failed to parse invoice</p>
              <p className="text-red-400/70 text-sm mt-1">{parseError}</p>
              <p className="text-gray-500 text-sm mt-2">You can still manually enter the purchase details below.</p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {uploadFile && !parsing && (
        <>
          {/* File info + reset */}
          <div className="flex items-center justify-between bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              {uploadFile.type?.startsWith('image/') ? (
                <ImageIcon size={20} className="text-blue-400" />
              ) : (
                <FileText size={20} className="text-blue-400" />
              )}
              <div>
                <p className="text-white text-sm font-medium">{uploadFile.name}</p>
                <p className="text-gray-500 text-xs">{(uploadFile.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {parseResult && (
                <button
                  onClick={() => setShowRawText(!showRawText)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-white px-2 py-1.5 rounded bg-gray-800 hover:bg-gray-700 transition"
                >
                  <Eye size={12} /> {showRawText ? 'Hide' : 'Show'} Raw Text
                </button>
              )}
              <button
                onClick={resetUpload}
                className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-2 py-1.5 rounded bg-gray-800 hover:bg-red-900/40 transition"
              >
                <X size={12} /> Remove
              </button>
            </div>
          </div>

          {/* Image preview */}
          {uploadPreview && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3 max-h-64 overflow-auto">
              <img src={uploadPreview} alt="Invoice preview" className="max-w-full rounded" />
            </div>
          )}

          {/* Raw text */}
          {showRawText && parseResult?.rawText && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-2 font-medium">Extracted Text</p>
              <pre className="text-xs text-gray-300 whitespace-pre-wrap max-h-48 overflow-auto font-mono bg-gray-950 rounded p-3">
                {parseResult.rawText}
              </pre>
            </div>
          )}

          {/* Detection Results Summary */}
          {parseResult && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={18} className="text-blue-400" />
                <h3 className="text-sm font-semibold text-white">Auto-Detected Information</h3>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {/* Supplier Detection */}
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Supplier</p>
                  {parseResult.supplierMatch ? (
                    <div>
                      <p className="text-white font-medium text-sm">{parseResult.supplierMatch.supplier.name}</p>
                      <p className={`text-xs ${confidenceColor(parseResult.supplierMatch.score)}`}>
                        {Math.round(parseResult.supplierMatch.score * 100)}% match (via {parseResult.supplierMatch.matchType})
                      </p>
                    </div>
                  ) : parseResult.extractedSupplierName ? (
                    <div>
                      <p className="text-orange-300 text-sm">"{parseResult.extractedSupplierName}"</p>
                      <p className="text-xs text-gray-500">Not in system</p>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Not detected</p>
                  )}
                </div>

                {/* Date Detection */}
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Date</p>
                  <p className={`text-sm font-medium ${parseResult.date ? 'text-white' : 'text-gray-500'}`}>
                    {parseResult.date || 'Not detected'}
                  </p>
                </div>

                {/* Total Detection */}
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Total Amount</p>
                  <p className={`text-sm font-medium ${parseResult.total ? 'text-white' : 'text-gray-500'}`}>
                    {parseResult.total ? `R${parseResult.total.toFixed(2)}` : 'Not detected'}
                  </p>
                </div>
              </div>

              {/* Line items count */}
              {parseResult.lineItems?.length > 0 && (
                <p className="text-xs text-green-400 mt-3">
                  {parseResult.lineItems.length} line item{parseResult.lineItems.length !== 1 ? 's' : ''} extracted
                </p>
              )}
            </div>
          )}

          {/* Supplier Selection / Creation */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-white mb-4">Supplier</h3>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <select
                  value={uploadSupplierId}
                  onChange={e => setUploadSupplierId(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-gray-500"
                >
                  <option value="">Select existing supplier...</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <button
                onClick={handleCreateSupplier}
                className="flex items-center gap-2 bg-green-900/50 hover:bg-green-900 border border-green-800 text-green-300 px-4 py-2.5 rounded-lg text-sm font-medium transition whitespace-nowrap"
              >
                <UserPlus size={16} /> New Supplier
              </button>
            </div>

            {/* New Supplier Inline Form */}
            {newSupplierForm && (
              <div className="mt-4 bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-3">
                <p className="text-xs text-gray-400 font-medium">Create New Supplier from Invoice</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Name *</label>
                    <input
                      type="text"
                      value={newSupplierForm.name}
                      onChange={e => setNewSupplierForm({ ...newSupplierForm, name: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                      placeholder="Supplier name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Category</label>
                    <select
                      value={newSupplierForm.category}
                      onChange={e => setNewSupplierForm({ ...newSupplierForm, category: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                    >
                      {SUPPLIER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={newSupplierForm.phone}
                      onChange={e => setNewSupplierForm({ ...newSupplierForm, phone: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                      placeholder="012 345 6789"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Contact Person</label>
                    <input
                      type="text"
                      value={newSupplierForm.contactPerson}
                      onChange={e => setNewSupplierForm({ ...newSupplierForm, contactPerson: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                      placeholder="Full name"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setNewSupplierForm(null)}
                    className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNewSupplier}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-medium rounded-lg transition"
                  >
                    <Check size={12} /> Create & Select
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Date */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Date</h3>
            <input
              type="date"
              value={uploadDate}
              onChange={e => setUploadDate(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
            />
          </div>

          {/* Line Items */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white">Line Items</h3>
              {parseResult?.lineItems?.length > 0 && (
                <span className="text-xs text-blue-400">{parseResult.lineItems.length} auto-extracted</span>
              )}
            </div>

            <div className="space-y-2">
              {uploadLineItems.map((li, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    value={li.inventoryItemId}
                    onChange={e => updateUploadLineItem(idx, 'inventoryItemId', e.target.value)}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                  >
                    <option value="">Select item...</option>
                    {allItems.map(item => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={li.name}
                    onChange={e => updateUploadLineItem(idx, 'name', e.target.value)}
                    placeholder="Or type name"
                    className="w-32 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                  />
                  <input
                    type="number"
                    value={li.qty}
                    onChange={e => updateUploadLineItem(idx, 'qty', e.target.value)}
                    placeholder="Qty"
                    className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={li.unitPrice}
                    onChange={e => updateUploadLineItem(idx, 'unitPrice', e.target.value)}
                    placeholder="Price"
                    className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                  />
                  <span className="text-gray-400 text-sm w-20 text-right">R{lineTotal(li).toFixed(2)}</span>
                  <button
                    onClick={() => removeUploadLineItem(idx)}
                    className="p-1.5 text-gray-500 hover:text-red-400 transition"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={addUploadLineItem}
              className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs mt-2 transition"
            >
              <Plus size={14} /> Add line item
            </button>

            {/* Total */}
            <div className="flex items-center justify-between border-t border-gray-800 pt-4 mt-4">
              <div>
                <p className="text-xs text-gray-400">Total</p>
                <p className="text-xl font-bold text-white">
                  R{uploadLineItems.reduce((s, li) => s + lineTotal(li), 0).toFixed(2)}
                </p>
                {parseResult?.total && (
                  <p className="text-xs text-gray-500">
                    Invoice total: R{parseResult.total.toFixed(2)}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSavePurchase('draft')}
                  className="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition"
                >
                  Save as Draft
                </button>
                <button
                  onClick={() => handleSavePurchase('confirmed')}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition"
                >
                  <Check size={14} /> Confirm & Update Stock
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
